/**
 * The shared viewer, wired to an open case.
 *
 * Base/overlay selection lives in `viewerStore` (`baseNodeId`/`overlayNodeIds`)
 * rather than local state, so it survives navigating away and matches whatever
 * the ribbon or Study Explorer already selected — this component only adds a
 * default 2D reading mode in front of the NiiVue grid that was there before.
 */

import * as React from "react";

import { getAssetUrl } from "@/ipc/api";
import { useAppStore } from "@/store/appStore";
import { ViewportGrid } from "./ViewportGrid";
import { SharedVolumeViewer, type SharedVolumeFile, type ViewerMode } from "./SharedVolumeViewer";
import { useResolvedVolumeUrls } from "./useResolvedVolumeUrls";
import { groupedNodes, nodeAbsPath, useViewerStore } from "./viewerStore";

/** Only the kinds that are actually a picture to read. */
const PICTURABLE = new Set(["image", "mask", "atlas"]);

export function CaseSharedViewer() {
  const manifest = useAppStore((s) => s.currentCase);
  const baseNodeId = useViewerStore((s) => s.baseNodeId);
  const overlayNodeIds = useViewerStore((s) => s.overlayNodeIds);
  const setBaseNode = useViewerStore((s) => s.setBaseNode);
  const toggleOverlayNode = useViewerStore((s) => s.toggleOverlayNode);
  const [mode, setMode] = React.useState<ViewerMode>("slices");

  const files = React.useMemo<SharedVolumeFile[]>(() => {
    if (!manifest) return [];
    return groupedNodes(manifest)
      .filter((g) => PICTURABLE.has(g.node.kind))
      .map(({ node }) => ({
        id: node.id,
        label: node.label,
        resolve: async () => {
          const abs = nodeAbsPath(manifest, node);
          if (!abs) return null;
          const { url } = await getAssetUrl(abs);
          return url || null;
        },
      }));
  }, [manifest]);

  // The first image a case opens with is a reasonable default read.
  React.useEffect(() => {
    if (!baseNodeId && files.length > 0) setBaseNode(files[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const { baseUrl, overlays } = useResolvedVolumeUrls(files, baseNodeId, overlayNodeIds);

  return (
    <SharedVolumeViewer
      files={files}
      baseId={baseNodeId}
      overlayIds={overlayNodeIds}
      onBaseChange={setBaseNode}
      onOverlayToggle={toggleOverlayNode}
      baseUrl={baseUrl}
      overlays={overlays}
      mode={mode}
      onModeChange={setMode}
      renderNiivue={() => <ViewportGrid />}
    />
  );
}
